"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useReducedMotion } from "@/lib/useReducedMotion";
import { getImageProps } from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { HeroSlide } from "@/lib/hero";

/** Header height (h-[68px]) - the hero fills everything below it. */
const HEADER_H = "68px";

/**
 * Responsive background for one slide: the browser picks the image for the screen size
 * (mobile < 640px, tablet ≥ 640px, laptop ≥ 1024px, desktop ≥ 1440px).
 */
function SlideImage({ slide, eager }: { slide: HeroSlide; eager: boolean }) {
  const { desktop, laptop, tablet, mobile } = slide.images;
  // next/image resizes + converts to WebP/AVIF per breakpoint; the admin uploads can be several MB each.
  const common = { alt: slide.alt ?? "", fill: true, sizes: "100vw", quality: 70, loading: eager ? ("eager" as const) : ("lazy" as const), fetchPriority: eager ? ("high" as const) : ("auto" as const) };
  const { props: desktopProps } = getImageProps({ ...common, src: desktop });
  const { props: laptopProps } = getImageProps({ ...common, src: laptop });
  const { props: tabletProps } = getImageProps({ ...common, src: tablet });
  const { props: mobileProps } = getImageProps({ ...common, src: mobile });
  return (
    <picture>
      <source media="(min-width: 1440px)" srcSet={desktopProps.srcSet} />
      <source media="(min-width: 1024px)" srcSet={laptopProps.srcSet} />
      <source media="(min-width: 640px)" srcSet={tabletProps.srcSet} />
      {/* eslint-disable-next-line @next/next/no-img-element -- art-directed <picture>, built from getImageProps */}
      <img {...mobileProps} alt={mobileProps.alt ?? ""} className="absolute inset-0 h-full w-full object-cover" />
    </picture>
  );
}

/**
 * Full-viewport hero fed by the admin "Hero Banners" module: several slides, each with its own image per screen
 * size, title, description and display time. `eyebrow`, `breadcrumb` and `children` stay the page's own;
 * a slide without a title/description shows no text at all.
 */
export function HeroSlider({
  slides,
  eyebrow,
  title,
  description,
  breadcrumb,
  children,
  compact = false,
}: {
  slides: HeroSlide[];
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  breadcrumb?: ReactNode;
  children?: ReactNode;
  /** Shorter banner that sits above page content (login pages) instead of filling the screen. */
  compact?: boolean;
}) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const count = slides.length;
  const go = useCallback((n: number) => setI((n + count) % count), [count]);
  const slide = slides[Math.min(i, count - 1)];

  // Crossfade without a animation library: the new slide fades in (CSS) over the one it replaces, which is
  // dropped once the fade is done. The first slide is painted as-is so the hero image is never hidden at load.
  const [shown, setShown] = useState(slide);
  const [leaving, setLeaving] = useState<HeroSlide | null>(null);
  const [animate, setAnimate] = useState(false);
  if (shown.id !== slide.id) {
    setLeaving(shown);
    setShown(slide);
    setAnimate(true);
  }
  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => setLeaving(null), 900);
    return () => clearTimeout(t);
  }, [leaving]);

  // Autoplay starts at the visitor's first interaction: a later slide painting in during the load would
  // otherwise become the page's Largest Contentful Paint and wreck the score.
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (engaged) return;
    const events = ["pointermove", "pointerdown", "keydown", "scroll", "touchstart"] as const;
    const onFirst = () => {
      events.forEach((e) => window.removeEventListener(e, onFirst));
      setEngaged(true);
    };
    events.forEach((e) => window.addEventListener(e, onFirst, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onFirst));
  }, [engaged]);

  // each slide stays for its own display time
  useEffect(() => {
    if (count < 2 || paused || reduce || !engaged) return;
    const t = setTimeout(() => setI((v) => (v + 1) % count), slide.duration * 1000);
    return () => clearTimeout(t);
  }, [i, count, paused, reduce, engaged, slide.duration]);

  return (
    <section
      className={`relative flex w-full overflow-hidden bg-stone-200 ${compact ? "pb-24 pt-12 sm:pb-32 sm:pt-16" : ""}`}
      style={compact ? undefined : { height: `calc(100svh - ${HEADER_H})`, minHeight: "30rem" }}
      role="region"
      aria-label="Featured banner"
      aria-roledescription={count > 1 ? "carousel" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {leaving && (
        <div key={`out-${leaving.id}`} className="absolute inset-0 brightness-[0.6]" aria-hidden>
          <SlideImage slide={leaving} eager={false} />
        </div>
      )}
      <div key={slide.id} className={`absolute inset-0 brightness-[0.6] ${animate ? "hero-in" : ""}`}>
        <SlideImage slide={slide} eager={i === 0} />
      </div>
      {/* <div className="absolute inset-0 bg-brand-900/55" aria-hidden /> */}

      {/* text only appears when the slide itself has a title or description - otherwise just the image */}
      {(slide.title || slide.description || children) && (
        <div className={`relative z-10 flex items-center ${compact ? "w-full" : "h-full"}`}>
          <div className="w-full px-[clamp(1.25rem,6vw,7rem)]">
            <div className="max-w-4xl">
              <div key={slide.id} className={animate ? "hero-text-in" : undefined}>
                  {slide.title && (
                    <h1 className={`mt-5 font-display font-medium leading-[1.04] !text-white text-balance ${compact ? "text-4xl sm:text-5xl" : "text-[2.6rem] sm:text-6xl lg:text-7xl"}`}>
                      {slide.title}
                    </h1>
                  )}
                  {slide.description && <p className="mt-5 max-w-xl text-lg text-white/90 text-pretty">{slide.description}</p>}
              </div>
              {children && <div className="mt-8">{children}</div>}
            </div>
          </div>
        </div>
      )}

      {count > 1 && (
        <>
          <button type="button" aria-label="Previous slide" onClick={() => go(i - 1)} className="absolute left-3 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white transition hover:bg-brand-600 sm:flex lg:left-6">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button type="button" aria-label="Next slide" onClick={() => go(i + 1)} className="absolute right-3 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white transition hover:bg-brand-600 sm:flex lg:right-6">
            <ChevronRight className="h-6 w-6" />
          </button>
          <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {slides.map((s, n) => (
              <button key={s.id} type="button" aria-label={`Go to slide ${n + 1}`} aria-current={n === i} onClick={() => go(n)} className="flex h-6 items-center justify-center px-2"><span className={`block h-2 rounded-full transition-all ${n === i ? "w-8 bg-gold-400" : "w-2 bg-white/60"}`} /></button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
